/**
 * Animation Configuration System
 * Based on ENHANCED_SYSTEM_PROMPT.md specifications
 * 
 * This module provides consistent animation timing and easing
 * across the entire application for a professional, trust-building experience.
 */

// ============================================
// ANIMATION TIMING CURVES
// ============================================

/**
 * Animation easing curves designed for different contexts.
 * All curves are optimized for professional, trust-building animations.
 */
export const ANIMATION_CURVES = {
    /** Material Design standard - professional and trustworthy */
    professional: 'cubicBezier(0.4, 0.0, 0.2, 1)',

    /** Gentle acceleration - smooth and calming */
    smooth: 'cubicBezier(0.25, 0.46, 0.45, 0.94)',

    /** Elastic out - for attention-grabbing elements */
    emphasis: 'cubicBezier(0.68, -0.55, 0.265, 1.55)',

    /** Quick bounce - for interactive feedback */
    snap: 'cubicBezier(0.34, 1.56, 0.64, 1)',

    /** Fast deceleration - for stat reveals and data */
    easeOutExpo: 'cubicBezier(0.16, 1, 0.3, 1)',

    /** Linear - for progress bars and counters */
    linear: 'linear',
} as const;

// CSS-compatible versions for inline styles
export const CSS_CURVES = {
    professional: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
    smooth: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    emphasis: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    snap: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    easeOutExpo: 'cubic-bezier(0.16, 1, 0.3, 1)',
    linear: 'linear',
} as const;

// ============================================
// ANIMATION DURATIONS
// ============================================

/**
 * Standard duration values in milliseconds.
 * Chosen based on research for optimal UX perception.
 */
export const DURATIONS = {
    /** Hover states, button presses - instant feedback */
    micro: 150,

    /** Fade in/out, simple transitions */
    short: 300,

    /** Section entrances, card reveals */
    medium: 600,

    /** Hero animations, complex sequences */
    long: 1200,

    /** Number counting animations */
    counter: 2000,

    /** Story-driven sequences (landing page) */
    narrative: 3000,
} as const;

// ============================================
// STAGGER PATTERNS
// ============================================

/**
 * Stagger configurations for animating groups of elements.
 */
export const STAGGER = {
    /** Grid items - start from center outward */
    grid: {
        delay: 80,
        from: 'center' as const,
        direction: 'normal' as const,
    },

    /** List items - sequential top to bottom */
    list: {
        delay: 100,
        easing: 'easeOutQuad',
    },

    /** Timeline steps - slower, narrative pace */
    timeline: {
        delay: 200,
        easing: 'easeOutExpo',
    },

    /** Dashboard cards */
    cards: {
        delay: 100,
        easing: 'easeOutExpo',
    },
} as const;

// ============================================
// ANIMATION PRESETS
// ============================================

/**
 * Pre-configured animation settings for common use cases.
 */
export const PRESETS = {
    /** Fade in with slide up - generic entrance */
    fadeInUp: {
        opacity: [0, 1],
        translateY: [20, 0],
        duration: DURATIONS.medium,
        easing: ANIMATION_CURVES.easeOutExpo,
    },

    /** Fade in with slide from left */
    fadeInLeft: {
        opacity: [0, 1],
        translateX: [-50, 0],
        duration: DURATIONS.medium,
        easing: ANIMATION_CURVES.easeOutExpo,
    },

    /** Fade in with slide from right */
    fadeInRight: {
        opacity: [0, 1],
        translateX: [50, 0],
        duration: DURATIONS.medium,
        easing: ANIMATION_CURVES.easeOutExpo,
    },

    /** Scale up with fade - for cards and modals */
    scaleIn: {
        opacity: [0, 1],
        scale: [0.9, 1],
        duration: DURATIONS.short,
        easing: ANIMATION_CURVES.emphasis,
    },

    /** Pulse ring - for detection/alert visuals */
    pulseRing: {
        scale: [1, 1.2],
        opacity: [0.5, 0],
        duration: 2000,
        loop: true,
        easing: ANIMATION_CURVES.smooth,
    },
} as const;

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Calculate stagger delay based on grid position.
 * Used for center-out grid animations.
 */
export function getGridStaggerDelay(
    index: number,
    gridSize: number = 3,
    baseDelay: number = 80
): number {
    const row = Math.floor(index / gridSize);
    const col = index % gridSize;
    const center = (gridSize - 1) / 2;
    const distance = Math.sqrt(Math.pow(row - center, 2) + Math.pow(col - center, 2));
    return distance * baseDelay;
}

/**
 * Get animation duration adjusted for reduced motion.
 */
export function getAccessibleDuration(
    standardDuration: number,
    reducedMotion: boolean
): number {
    return reducedMotion ? 0 : standardDuration;
}

export type AnimationCurve = keyof typeof ANIMATION_CURVES;
export type Duration = keyof typeof DURATIONS;
export type StaggerPattern = keyof typeof STAGGER;
export type AnimationPreset = keyof typeof PRESETS;
